// Pricing sheet review (Phase 4 slice 2b).
//
// §0 — the page's one question: "Can I trust how this sheet was read, and which
// of these rates should enter the book?" Primary object: one review (a record).
// Primary verb: Accept, then Commit. Worst-case volume: ~380 blocks / 42,000
// rates from a single vehicle workbook.
//
// Archetype A (record cockpit): 1fr + context rail, one verdict number, a KPI
// strip, and a Do-Next queue. The rail carries no actions.
//
// The shape of the screen follows the shape of the decision, which is the whole
// argument for staging rates instead of writing them: the values are bit-exact,
// so the failure mode is never a mistyped number — it is a misread column map,
// which corrupts every row beneath it uniformly. So the reader is asked TWO
// questions in order, not 42,000 times one question:
//
//   1. Is this how the sheet should be read?  (the reading — writes no rate)
//   2. Do these blocks belong in the book?    (accept/reject per identity)
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDataTable, SpecraDetailPage, SpecraEmptyState, SpecraInlineBanner, SpecraKeyValueRow, SpecraMetricStrip, SpecraOverlayDrawer, SpecraPageHeader, SpecraSectionHeader, SpecraSurface, SpecraTextarea, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageBody } from '../../../components/layout';
import { useIsViewOnly } from '../../../contexts/authContext';
import { useLayoutStore } from '../../../lib/stores/LayoutStore';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { EquivalencyTable } from './EquivalencyTable';
import { useApplyPricingSheetReview, usePricingSheetReview, useReExtractPricingSheet, useResolvePricingSheetBlock, useResolvePricingSheetBlocks } from './hooks';
import { PEEK_ROWS, PricingSheetPeek } from './PricingSheetPeek';
import { PricingSheetSourceDrawer } from './PricingSheetSourceDrawer';
import { ShowOnPageButton } from './ShowOnPageButton';
// Severity → badge variant. Rectangular tinted badges, never pills or dots, and
// colour ONLY here plus the alert band below.
const SEVERITY_VARIANT = {
    Blocking: 'danger',
    High: 'orange',
    Moderate: 'info',
    Informational: 'neutral'
};
// A block is settled once somebody has ruled on it. Matches the states pangaea
// will actually apply (Accepted | Corrected | Closed) plus the two refusals,
// because a rejected block is decided even though it writes nothing.
const SETTLED = new Set([
    'Accepted',
    'Corrected',
    'Closed',
    'Rejected',
    'Waived'
]);
function isSettled(state) {
    return SETTLED.has(state);
}
export function PricingSheetReviewPage() {
    // Suppress the legacy metadata band, exactly as RegistriesPage does. This page
    // is routed outside DeliveryLayout and owns its own header — without this it
    // inherited the default one and announced itself as "Dashboard".
    const setShowHeader = useLayoutStore((state)=>state.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    const { changeSetId = '' } = useParams();
    const companyId = useTenantId();
    const navigate = useNavigate();
    const viewOnly = useIsViewOnly();
    const { data, isLoading } = usePricingSheetReview(companyId, changeSetId);
    const resolveBlock = useResolvePricingSheetBlock();
    const commit = useApplyPricingSheetReview();
    const reread = useReExtractPricingSheet();
    const resolveMany = useResolvePricingSheetBlocks();
    // The drawer derives its record from a stored ID, never from a copy of the
    // row: syncing a selected-item object against refreshed data is what made an
    // earlier drawer loop forever.
    const [openBlockId, setOpenBlockId] = useState(null);
    // Which block a person asked to SEE. Separate from the block drawer's own
    // selection: they may open the page from a reading, which has no drawer, and
    // closing the page must not undo the decision they were in the middle of.
    const [sourceView, setSourceView] = useState(null);
    const [note, setNote] = useState('');
    const blocks = data?.blocks ?? [];
    const categories = data?.categories ?? [];
    const registryRows = data?.registryRows ?? [];
    const rateRows = data?.rateRows ?? [];
    const periods = data?.periods ?? [];
    const periodSpans = data?.periodSpans ?? [];
    // The document states its substitution rule once and it governs every
    // category, so it is shown once — not repeated down 54 rows.
    const equivalency = categories.find((c)=>c.equivalency)?.equivalency ?? '';
    const equivalencyPage = categories.find((c)=>c.equivalency)?.equivalencyPage ?? 0;
    const equivalencyBlockId = categories.find((c)=>c.equivalency)?.equivalencyBlockId ?? '';
    const readings = data?.readings ?? [];
    const summary = data?.summary;
    const openBlock = blocks.find((b)=>b.itemId === openBlockId) ?? null;
    // Readings shown INLINE are the ones still needing a decision, plus enough
    // confirmed ones to see the shape. A six-sheet document put six full readings
    // above the work — every one a wall of axis prose — and the confirmed ones are
    // exactly the ones nobody needs to read again.
    const [openReadingId, setOpenReadingId] = useState(null);
    const peekReadings = useMemo(()=>{
        const open = readings.filter((r)=>!isSettled(r.resolutionState));
        return open.length > 0 ? open.slice(0, PEEK_ROWS) : readings.slice(0, PEEK_ROWS);
    }, [
        readings
    ]);
    const openReading = readings.find((r)=>r.itemId === openReadingId) ?? null;
    // Clicks inside the page viewer are NOT "outside the drawer" in any sense a
    // person would recognise: the two panels are one workflow, side by side. The
    // viewer is portaled to the document body, so Radix sees it as outside and
    // would dismiss on the first click — closing the very thing the reviewer just
    // opened, and the drawer under it.
    const keepOpenWhenInteractingWithViewer = (event)=>{
        const el = event.target;
        if (el?.closest?.(`[data-testid="${Testids.pricingSheetReview.sourceDrawer}"]`)) {
            event.preventDefault?.();
        }
    };
    // A block can be SHOWN only when the document has pages and this block's rates
    // recorded which one they came from. A workbook has neither.
    const canShowBlockSource = Boolean(openBlock && summary?.sourceDocumentId && openBlock.sourcePage > 0);
    const unsettled = useMemo(()=>blocks.filter((b)=>!isSettled(b.resolutionState)), [
        blocks
    ]);
    const unconfirmedReadings = useMemo(()=>readings.filter((r)=>!isSettled(r.resolutionState)), [
        readings
    ]);
    const unsettledCategories = useMemo(()=>categories.filter((c)=>!isSettled(c.resolutionState)), [
        categories
    ]);
    // The FIRST apply tier, and the one that had no surface: the contract and the
    // contractor. Everything else is scoped to them, so an unruled row here makes
    // the whole apply a no-op — which is exactly what happened.
    const unsettledRegistry = useMemo(()=>registryRows.filter((r)=>!isSettled(r.resolutionState)), [
        registryRows
    ]);
    const unsettledBlocks = useMemo(()=>blocks.filter((b)=>!isSettled(b.resolutionState)), [
        blocks
    ]);
    // How many RATES the bulk accept covers. "Accept all 25" and "accept all 25
    // (277 rates)" are the same click and very different amounts of consequence.
    const unsettledRates = useMemo(()=>unsettledBlocks.reduce((n, b)=>n + b.rates, 0), [
        unsettledBlocks
    ]);
    // Proposed categories the document never defined. These are the ones worth a
    // look before they enter the catalog, and they are what "need attention"
    // should have been counting all along.
    const undefinedCategories = useMemo(()=>categories.filter((c)=>!c.qualifications && !c.description).length, [
        categories
    ]);
    const unresolvedCategories = useMemo(()=>blocks.reduce((n, b)=>n + b.unresolved, 0), [
        blocks
    ]);
    // The commit gate. Two rules, and the SECOND one is the reason this page
    // exists: accepting rate blocks while the sheet's reading is still unconfirmed
    // means approving 12,000 numbers without agreeing what their columns meant.
    //
    // Enforced here rather than in the server's apply, which today would honour a
    // direct call — noted as a real gap rather than papered over.
    // A proposed CATEGORY is part of the gate too: a rate whose category was not
    // ruled on cannot be applied, so committing with categories still open would
    // silently drop the rates waiting on them.
    //
    // The REGISTRY ROWS are part of the gate, and leaving them out is what let a
    // review be spent on an apply that could write nothing: the vehicle and the
    // contractor sat Open with no surface, Commit was enabled anyway, and the apply
    // declined all 54 categories with "its contractor has not been accepted" while
    // the change set went to Applied. A gate that permits an unappliable commit is
    // worse than a missing control, because it looks like it worked.
    const commitBlocked = viewOnly || unsettledRegistry.length > 0 || unsettled.length > 0 || unsettledCategories.length > 0 || unconfirmedReadings.length > 0 || commit.isPending;
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                title: "Loading review…"
            })
        });
    }
    if (!summary) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                title: "No such pricing sheet review",
                description: "It may have been committed already, or belong to another tenant.",
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>navigate('/delivery/registries?tab=pricing-sheets'),
                    children: "Back to pricing sheets"
                })
            })
        });
    }
    const ruleOn = (itemId, resolution)=>{
        resolveBlock.mutate({
            companyId,
            changeSetId,
            itemId,
            resolution,
            note
        }, {
            onSuccess: ()=>{
                setOpenBlockId(null);
                setNote('');
            }
        });
    };
    // Readings as a TABLE, not as stacked sections.
    //
    // Each reading is six lines of axis prose, and a document has one per sheet. At
    // three sheets that was most of the page; at a hundred it is unusable. The row
    // carries what decides the question — which sheet, what the columns meant, and
    // whether the sheet's own arithmetic agrees — and the full reading is a click
    // away. Vertical space is the scarce resource on a report.
    const renderReadings = (rows)=>/*#__PURE__*/ _jsx(SpecraDataTable, {
            columns: [
                {
                    id: 'sheet',
                    header: 'Sheet',
                    cell: (r)=>/*#__PURE__*/ _jsx("span", {
                            className: "whitespace-nowrap",
                            children: r.sheetName || '—'
                        })
                },
                {
                    id: 'readAs',
                    header: 'Read as',
                    // ONE line, the period axis, hover for the rest.
                    //
                    // The axes are sentences in the parser's own words — "each rate column
                    // is a period (5 distinct)" — and three of those per row wrapped to
                    // five lines each, which is the wall of text this table replaced. The
                    // period axis is the one that decides whether the reading is right;
                    // company is the same on every sheet of a pricelist, and all of it is
                    // in the row's drawer.
                    cell: (r)=>/*#__PURE__*/ _jsx("span", {
                            className: "line-clamp-1 text-muted-foreground",
                            title: [
                                r.companyAxis,
                                r.periodAxis,
                                r.worksiteAxis
                            ].filter(Boolean).join(' · '),
                            children: r.periodAxis || r.companyAxis || '—'
                        })
                },
                {
                    align: 'right',
                    id: 'rates',
                    header: 'Rates',
                    cell: (r)=>r.rateCount.toLocaleString()
                },
                {
                    id: 'flags',
                    header: 'Watch',
                    // The two things that argue with a reading: the sheet's own arithmetic,
                    // and what was deliberately not extracted. Counted here, read in full
                    // in the drawer.
                    cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap gap-1",
                            children: [
                                r.validation.includes('FAILED') ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "danger",
                                    children: "Self-check failed"
                                }) : null,
                                r.refusals.length > 0 ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                                    variant: "warning",
                                    children: [
                                        r.refusals.length,
                                        " not extracted"
                                    ]
                                }) : null
                            ]
                        })
                },
                {
                    id: 'state',
                    header: 'Reading',
                    cell: (r)=>isSettled(r.resolutionState) ? /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "success",
                            children: "Confirmed"
                        }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "neutral",
                            children: "Open"
                        })
                },
                {
                    id: 'source',
                    header: '',
                    cell: (r)=>summary.sourceDocumentId && r.sourcePage > 0 ? /*#__PURE__*/ _jsx(ShowOnPageButton, {
                            onClick: ()=>setSourceView({
                                    blockId: r.sourceBlockId,
                                    page: r.sourcePage
                                }),
                            page: r.sourcePage,
                            testId: `${Testids.pricingSheetReview.viewSource}-reading-${r.itemId}`,
                            what: "this sheet"
                        }) : null
                },
                {
                    id: 'actions',
                    header: '',
                    cell: (r)=>viewOnly || isSettled(r.resolutionState) ? null : /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": `${Testids.pricingSheetReview.readingConfirm}-${r.itemId}`,
                            disabled: resolveBlock.isPending,
                            onClick: ()=>ruleOn(r.itemId, 'Accepted'),
                            variant: "outline",
                            children: "Confirm"
                        })
                }
            ],
            "data-testid": Testids.pricingSheetReview.readingsTable,
            emptyTitle: "No readings",
            getRowActionLabel: (r)=>`Open the full reading of ${r.sheetName || 'this sheet'}`,
            getRowId: (r)=>r.itemId,
            onRowClick: (r)=>setOpenReadingId(r.itemId),
            rowTestId: (r)=>`${Testids.pricingSheetReview.readingRow}-${r.itemId}`,
            rows: rows
        });
    const renderRegistryRows = (rows)=>/*#__PURE__*/ _jsx(SpecraDataTable, {
            columns: [
                {
                    id: 'kind',
                    header: 'What',
                    cell: (r)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: r.kind === 'ContractVehicle' ? 'info' : 'neutral',
                            children: r.kind === 'ContractVehicle' ? 'Contract' : 'Contractor'
                        })
                },
                {
                    id: 'name',
                    header: 'Name',
                    cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    children: r.name
                                }),
                                r.detail ? /*#__PURE__*/ _jsx("div", {
                                    className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                    children: r.detail
                                }) : null
                            ]
                        })
                },
                {
                    id: 'identifier',
                    header: 'Government key',
                    // The absence is the interesting case: a contract number or a UEI is the
                    // government's exact handle, and a row with only a name may be something
                    // you already have under a different spelling.
                    cell: (r)=>r.identifier ? /*#__PURE__*/ _jsx("span", {
                            className: "font-mono text-[length:var(--fs-ui-caption)]",
                            children: r.identifier
                        }) : /*#__PURE__*/ _jsx("span", {
                            className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                            children: "none stated"
                        })
                },
                {
                    id: 'why',
                    header: 'Why',
                    cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                            className: "max-w-[46ch]",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "text-[length:var(--fs-ui-caption)]",
                                    children: r.reason
                                }),
                                r.source ? /*#__PURE__*/ _jsxs("div", {
                                    className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                    children: [
                                        "Read from ",
                                        r.source
                                    ]
                                }) : null
                            ]
                        })
                },
                {
                    id: 'state',
                    header: 'Decision',
                    cell: (r)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: r.resolutionState === 'Accepted' ? 'success' : r.resolutionState === 'Rejected' ? 'danger' : 'neutral',
                            children: r.resolutionState
                        })
                },
                {
                    id: 'actions',
                    header: '',
                    cell: (r)=>viewOnly || isSettled(r.resolutionState) ? null : /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-end gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": `${Testids.pricingSheetReview.registryAccept}-${r.itemId}`,
                                    disabled: resolveBlock.isPending,
                                    onClick: ()=>ruleOn(r.itemId, 'Accepted'),
                                    children: "Accept"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": `${Testids.pricingSheetReview.registryReject}-${r.itemId}`,
                                    disabled: resolveBlock.isPending,
                                    onClick: ()=>ruleOn(r.itemId, 'Rejected'),
                                    variant: "outline",
                                    children: "Reject"
                                })
                            ]
                        })
                }
            ],
            "data-testid": Testids.pricingSheetReview.registryTable,
            emptyTitle: "No new registry rows",
            getRowId: (r)=>r.itemId,
            rowTestId: (r)=>`${Testids.pricingSheetReview.registryRow}-${r.itemId}`,
            rows: rows
        });
    const renderRateRows = (rows)=>/*#__PURE__*/ _jsx(SpecraDataTable, {
            columns: [
                {
                    id: 'lcat',
                    header: 'Labor category',
                    cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    children: r.laborCategory
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                    children: [
                                        r.sin && `SIN ${r.sin}`,
                                        r.worksite,
                                        r.burdenBasis
                                    ].filter(Boolean).join(' · ')
                                })
                            ]
                        })
                },
                // One column per period, in DOCUMENT order — sorting these would
                // put Year 10 between Year 1 and Year 2.
                ...periods.map((p, i)=>({
                        align: 'right',
                        id: `period-${i}`,
                        header: (()=>{
                            const span = periodSpans[i];
                            if (!span?.startDate || !span?.endDate) {
                                return p;
                            }
                            // The dates under the label. "Year 3" compares to nothing
                            // across vendors; a dated span compares exactly, and this
                            // document states them right in the column header.
                            return /*#__PURE__*/ _jsxs("div", {
                                className: "text-right",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        children: p
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "font-normal text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                        children: [
                                            span.startDate,
                                            " – ",
                                            span.endDate
                                        ]
                                    })
                                ]
                            });
                        })(),
                        cell: (r)=>{
                            const cell = r.cells[i];
                            if (!cell?.present) {
                                // An explicit hole. A role priced in four of five years is a
                                // real finding, and a blank would read as "nothing to see".
                                return /*#__PURE__*/ _jsx("span", {
                                    className: "text-muted-foreground",
                                    children: "—"
                                });
                            }
                            const amount = cell.amount.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            });
                            // The NUMBER is the click target, per rate. An eye in every cell
                            // would be five icons a row across fifty-eight rows; the amount is
                            // what a reviewer is already pointing at, and each one knows its own
                            // page — Year 1 can be on page 4 and Year 5 on page 9, and a
                            // multipage schedule splits one role across tables.
                            if (!summary.sourceDocumentId || !cell.sourcePage) {
                                return /*#__PURE__*/ _jsx("span", {
                                    className: "tabular-nums",
                                    children: amount
                                });
                            }
                            return /*#__PURE__*/ _jsx(SpecraTooltip, {
                                content: `Show ${amount} on page ${cell.sourcePage}`,
                                children: /*#__PURE__*/ _jsx("button", {
                                    className: "tabular-nums underline decoration-dotted underline-offset-2 hover:decoration-solid",
                                    "data-testid": `${Testids.pricingSheetReview.viewSource}-rate-${r.laborCategory}-${cell.periodLabel}`,
                                    onClick: ()=>setSourceView({
                                            blockId: cell.sourceBlockId,
                                            page: cell.sourcePage
                                        }),
                                    type: "button",
                                    children: amount
                                })
                            });
                        }
                    }))
            ],
            "data-testid": Testids.pricingSheetReview.ratesTable,
            emptyTitle: "No rates staged",
            getRowId: (r)=>`${r.laborCategory}|${r.sin}|${r.worksite}|${r.burdenBasis}`,
            rows: rows
        });
    const renderBlocks = (rows)=>/*#__PURE__*/ _jsx(SpecraDataTable, {
            columns: [
                {
                    id: 'identity',
                    header: 'Block',
                    cell: (b)=>b.identity || '(no identity stated)'
                },
                {
                    id: 'severity',
                    header: 'Confidence',
                    cell: (b)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: SEVERITY_VARIANT[b.severity] ?? 'neutral',
                            children: b.severity || 'Unclassified'
                        })
                },
                {
                    id: 'state',
                    header: 'Decision',
                    cell: (b)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: b.resolutionState === 'Accepted' ? 'success' : b.resolutionState === 'Rejected' ? 'danger' : 'neutral',
                            children: b.resolutionState
                        })
                },
                {
                    id: 'sheet',
                    header: 'Sheet',
                    cell: (b)=>b.sheetName || '—'
                },
                {
                    align: 'right',
                    id: 'rates',
                    header: 'Rates',
                    cell: (b)=>b.rates.toLocaleString()
                },
                {
                    align: 'right',
                    id: 'unresolved',
                    header: 'Unregistered',
                    cell: (b)=>b.unresolved > 0 ? String(b.unresolved) : '—'
                }
            ],
            "data-testid": Testids.pricingSheetReview.blocksTable,
            emptyDescription: "This sheet produced no reviewable rates. Its refusals are listed with the reading above.",
            emptyTitle: "Nothing to review",
            getRowActionLabel: (b)=>`Review ${b.rates.toLocaleString()} rates: ${b.identity || 'unnamed block'}`,
            getRowId: (b)=>b.itemId,
            onRowClick: (b)=>{
                setOpenBlockId(b.itemId);
                setNote('');
            },
            rowTestId: (b)=>`${Testids.pricingSheetReview.blockRow}-${b.itemId}`,
            rows: rows
        });
    // One table body, rendered twice: five inline, all of them in the drawer. A
    // second copy of these columns would drift from this one within a week.
    // One table body, rendered twice: five inline, all of them in the drawer. A
    // second copy of these columns would drift from this one within a week.
    const renderCategories = (rows)=>/*#__PURE__*/ _jsx(SpecraDataTable, {
            columns: [
                {
                    id: 'title',
                    header: 'Labor category',
                    cell: (c)=>/*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    children: c.title
                                }),
                                c.clearance ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "info",
                                    children: c.clearance
                                }) : null
                            ]
                        })
                },
                {
                    id: 'definition',
                    header: 'What the document says it is',
                    cell: (c)=>c.description || c.qualifications ? /*#__PURE__*/ _jsxs("div", {
                            className: "max-w-[62ch] space-y-1",
                            children: [
                                c.description ? /*#__PURE__*/ _jsx("p", {
                                    className: "line-clamp-3 text-[length:var(--fs-ui-caption)]",
                                    title: c.description,
                                    children: c.description
                                }) : null,
                                c.qualifications ? /*#__PURE__*/ _jsx("p", {
                                    className: "whitespace-pre-line text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                    children: c.qualifications
                                }) : null,
                                c.clearance ? /*#__PURE__*/ _jsxs("p", {
                                    className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                    children: [
                                        "Clearance: ",
                                        c.clearance
                                    ]
                                }) : null,
                                c.definitionSource ? /*#__PURE__*/ _jsxs("p", {
                                    className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                    children: [
                                        "Read from ",
                                        c.definitionSource
                                    ]
                                }) : null
                            ]
                        }) : /*#__PURE__*/ _jsx("span", {
                            className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                            children: "The document never says — it only prices it"
                        })
                },
                {
                    id: 'source',
                    header: '',
                    cell: (c)=>summary.sourceDocumentId && c.sourcePage > 0 ? /*#__PURE__*/ _jsx(ShowOnPageButton, {
                            onClick: ()=>setSourceView({
                                    blockId: c.sourceBlockId,
                                    page: c.sourcePage
                                }),
                            page: c.sourcePage,
                            testId: `${Testids.pricingSheetReview.viewSource}-${c.itemId}`,
                            what: "this definition"
                        }) : null
                },
                {
                    id: 'state',
                    header: 'Decision',
                    cell: (c)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: c.resolutionState === 'Accepted' ? 'success' : c.resolutionState === 'Rejected' ? 'danger' : 'neutral',
                            children: c.resolutionState
                        })
                },
                {
                    align: 'right',
                    id: 'waiting',
                    header: 'Rates waiting',
                    cell: (c)=>c.waitingRates > 0 ? String(c.waitingRates) : '\u2014'
                },
                {
                    id: 'actions',
                    header: '',
                    cell: (c)=>viewOnly || isSettled(c.resolutionState) ? null : /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-end gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": `${Testids.pricingSheetReview.categoryAccept}-${c.itemId}`,
                                    disabled: resolveBlock.isPending,
                                    onClick: ()=>resolveBlock.mutate({
                                            changeSetId,
                                            companyId,
                                            itemId: c.itemId,
                                            resolution: 'Accepted'
                                        }),
                                    children: "Accept"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": `${Testids.pricingSheetReview.categoryReject}-${c.itemId}`,
                                    disabled: resolveBlock.isPending,
                                    onClick: ()=>resolveBlock.mutate({
                                            changeSetId,
                                            companyId,
                                            itemId: c.itemId,
                                            resolution: 'Rejected'
                                        }),
                                    variant: "outline",
                                    children: "Reject"
                                })
                            ]
                        })
                }
            ],
            "data-testid": Testids.pricingSheetReview.categoriesTable,
            emptyTitle: "No new labor categories",
            getRowId: (c)=>c.itemId,
            rowTestId: (c)=>`${Testids.pricingSheetReview.categoryRow}-${c.itemId}`,
            rows: rows
        });
    return /*#__PURE__*/ _jsxs(PageBody, {
        "data-testid": Testids.pricingSheetReview.page,
        children: [
            /*#__PURE__*/ _jsxs(SpecraDetailPage, {
                header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
                    eyebrow: "Pricing sheet review",
                    title: summary.title || 'Pricing sheet',
                    description: // Facts with numbers, and the consequence named before the action.
                    `${summary.rates.toLocaleString()} rates are staged across ${summary.blocks} review blocks. ` + 'Nothing enters the rate book until this review is committed.',
                    actions: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            summary.sourceDocumentId ? /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.pricingSheetReview.reread,
                                disabled: viewOnly || reread.isPending,
                                onClick: ()=>reread.mutate({
                                        companyId,
                                        companyProfileId: summary.companyProfileId,
                                        contractVehicleId: summary.contractVehicleId,
                                        observationType: summary.observationType,
                                        sourceDocumentId: summary.sourceDocumentId,
                                        supersedesChangeSetId: changeSetId
                                    }, {
                                        onSuccess: (runId)=>navigate(`/registries?tab=pricing-sheets&run=${runId}`)
                                    }),
                                variant: "outline",
                                children: reread.isPending ? 'Re-reading…' : 'Re-read this sheet'
                            }) : null,
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.pricingSheetReview.commit,
                                disabled: commitBlocked,
                                onClick: ()=>commit.mutate({
                                        companyId,
                                        changeSetId
                                    }),
                                children: "Commit review"
                            })
                        ]
                    }),
                    meta: /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: summary.state
                    })
                }),
                aside: // Context only — never actions.
                /*#__PURE__*/ _jsxs(SpecraSurface, {
                    className: "p-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            headingAs: "h3",
                            title: "Sheet"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-3",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                    label: "Staged by",
                                    value: summary.createdBy || '—'
                                }),
                                /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                    label: "Blocks",
                                    value: String(summary.blocks)
                                }),
                                /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                    label: "Awaiting a decision",
                                    value: String(unsettled.length)
                                }),
                                /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                    label: "New labor categories",
                                    value: String(categories.length)
                                }),
                                /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                    label: "Rates with no registry match",
                                    value: String(unresolvedCategories)
                                })
                            ]
                        })
                    ]
                }),
                children: [
                    /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                        "data-testid": Testids.pricingSheetReview.metricStrip,
                        metrics: [
                            // The verdict — the one big number on the page.
                            {
                                id: 'rates',
                                label: 'Rates staged',
                                value: summary.rates.toLocaleString()
                            },
                            {
                                id: 'blocks',
                                label: 'Review blocks',
                                value: String(summary.blocks)
                            },
                            {
                                id: 'categories',
                                label: 'New labor categories',
                                // Toned by whether any still need a decision, because an accepted
                                // roster and an untouched one are different situations.
                                tone: unsettledCategories.length > 0 ? 'warning' : 'neutral',
                                value: String(categories.length)
                            },
                            {
                                id: 'attention',
                                label: 'Need a definition',
                                // Counted across CATEGORIES, not just Blocking blocks. "Need
                                // attention 0" beside 42 flagged categories was simply false — the
                                // metric was reading a field the categories do not use.
                                tone: undefinedCategories > 0 ? 'danger' : 'neutral',
                                value: String(undefinedCategories)
                            }
                        ]
                    }),
                    unconfirmedReadings.length > 0 ? // The single alert band this page is allowed.
                    /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "warning",
                        children: [
                            "Confirm how ",
                            unconfirmedReadings.length === 1 ? 'this sheet was' : 'these sheets were',
                            ' ',
                            "read before committing. Accepting blocks first would approve the numbers without agreeing what their columns meant."
                        ]
                    }) : null,
                    summary.companyScope === 'SingleCompany' && summary.companiesNamed.length > 1 ? // Stated, never resolved. This document names a firm and its pre-merger
                    // name and the page does not say they are the same — but the next
                    // document may name two genuinely different contractors, and no rule on
                    // the names can tell those apart. So the reviewer is told, precisely.
                    /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        "data-testid": Testids.pricingSheetReview.companyAmbiguity,
                        tone: "warning",
                        children: [
                            "This document prices ONE company but names ",
                            summary.companiesNamed.length,
                            ":",
                            ' ',
                            summary.companiesNamed.join(', '),
                            ". Its rates were filed under",
                            ' ',
                            summary.companiesNamed[0],
                            ". Confirm that is the right one before committing — the document does not say whether these are the same firm."
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsx(PricingSheetPeek, {
                        description: "One row per sheet. Confirming a reading agrees what its columns MEANT — the decision every rate underneath it depends on. Open a row for the full reading.",
                        drawerEyebrow: summary.title || 'Pricing sheet',
                        renderAll: ()=>renderReadings(readings),
                        shown: peekReadings.length,
                        testId: Testids.pricingSheetReview.readingsSection,
                        title: "How each sheet was read",
                        total: readings.length,
                        viewAllTestId: Testids.pricingSheetReview.readingsViewAll,
                        children: renderReadings(peekReadings)
                    }),
                    registryRows.length > 0 ? /*#__PURE__*/ _jsx(PricingSheetPeek, {
                        action: viewOnly || unsettledRegistry.length === 0 ? null : /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.pricingSheetReview.acceptAllRegistry,
                            disabled: resolveMany.isPending,
                            onClick: ()=>resolveMany.mutate({
                                    changeSetId,
                                    companyId,
                                    itemIds: unsettledRegistry.map((r)=>r.itemId),
                                    resolution: 'Accepted'
                                }),
                            variant: "outline",
                            children: resolveMany.isPending ? 'Accepting…' : `Accept all ${unsettledRegistry.length}`
                        }),
                        description: "The contract these ceilings are binding on, and the contractor whose rates they are. Nothing else in this review can be applied until these exist — every category and rate below is scoped to them.",
                        drawerEyebrow: summary.title || 'Pricing sheet',
                        renderAll: ()=>renderRegistryRows(registryRows),
                        testId: Testids.pricingSheetReview.registrySection,
                        title: "New registry rows",
                        total: registryRows.length,
                        viewAllTestId: Testids.pricingSheetReview.registryViewAll,
                        children: renderRegistryRows(registryRows.slice(0, PEEK_ROWS))
                    }) : null,
                    equivalency ? // Stated once by the document and governing every category below, so
                    // shown once. Without it "Bachelors" reads as a hard bar; with it the
                    // same category is open to an associate's and four more years, which is
                    // the difference between a category you can staff and one you cannot.
                    /*#__PURE__*/ _jsx(SpecraSurface, {
                        className: "p-3",
                        "data-testid": Testids.pricingSheetReview.equivalency,
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-start justify-between gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "min-w-0",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-[length:var(--fs-ui-caption)] font-medium uppercase tracking-[var(--ls-ui-eyebrow)] text-muted-foreground",
                                            children: "Education and experience may be substituted"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mt-2",
                                            children: /*#__PURE__*/ _jsx(EquivalencyTable, {
                                                text: equivalency
                                            })
                                        })
                                    ]
                                }),
                                summary.sourceDocumentId && equivalencyPage > 0 ? /*#__PURE__*/ _jsx(ShowOnPageButton, {
                                    onClick: ()=>setSourceView({
                                            blockId: equivalencyBlockId,
                                            page: equivalencyPage
                                        }),
                                    page: equivalencyPage,
                                    testId: Testids.pricingSheetReview.equivalencySource,
                                    what: "the substitution table"
                                }) : null
                            ]
                        })
                    }) : null,
                    categories.length > 0 ? /*#__PURE__*/ _jsx(PricingSheetPeek, {
                        action: viewOnly ? null : /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.pricingSheetReview.acceptAllCategories,
                            disabled: resolveMany.isPending || unsettledCategories.length === 0,
                            onClick: ()=>resolveMany.mutate({
                                    changeSetId,
                                    companyId,
                                    // EVERY undecided category, not only the defined ones.
                                    //
                                    // This used to filter to defined-only, reasoning that a bare
                                    // title deserves a look. On a real MAS pricelist NONE are
                                    // defined, so the button matched zero and did nothing
                                    // silently — a dead control, which is worse than the problem
                                    // it avoided. The count of undefined ones is in the label and
                                    // the reason is on every row, so accepting is an informed
                                    // choice rather than a hidden one.
                                    itemIds: unsettledCategories.map((c)=>c.itemId),
                                    resolution: 'Accepted'
                                }),
                            variant: "outline",
                            children: resolveMany.isPending ? 'Accepting…' : undefinedCategories > 0 ? `Accept all ${unsettledCategories.length} (${undefinedCategories} undefined)` : `Accept all ${unsettledCategories.length}`
                        }),
                        description: "These jobs are not in your registry yet. A rate cannot be applied to a category you have not accepted.",
                        drawerEyebrow: summary.title || 'Pricing sheet',
                        renderAll: ()=>renderCategories(categories),
                        testId: Testids.pricingSheetReview.categoriesSection,
                        title: "New labor categories",
                        total: categories.length,
                        viewAllTestId: Testids.pricingSheetReview.categoriesViewAll,
                        children: renderCategories(categories.slice(0, PEEK_ROWS))
                    }) : null,
                    rateRows.length > 0 ? /*#__PURE__*/ _jsx(PricingSheetPeek, {
                        description: "One row per role and schedule line, one column per period — so a jump between years, or a role priced in some years and not others, is visible.",
                        drawerEyebrow: summary.title || 'Pricing sheet',
                        renderAll: ()=>renderRateRows(rateRows),
                        testId: Testids.pricingSheetReview.ratesSection,
                        title: "Rates by labor category",
                        total: rateRows.length,
                        viewAllTestId: Testids.pricingSheetReview.ratesViewAll,
                        children: renderRateRows(rateRows.slice(0, PEEK_ROWS))
                    }) : null,
                    /*#__PURE__*/ _jsx(PricingSheetPeek, {
                        action: viewOnly ? null : // Bulk accept, because per-block was the wrong unit of work.
                        //
                        // A block is bit-exact by construction — pricingsheet.Stage's
                        // argument is that the failure mode is never a mistyped number, it
                        // is a misread column map, and THAT is what the readings above
                        // confirm. So opening twenty-five drawers to accept twenty-five
                        // blocks asks a reviewer to re-make one decision twenty-five times,
                        // and they will stop after ten. The label says how many rates ride
                        // on the click, and the commit gate still refuses until every
                        // reading is confirmed.
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.pricingSheetReview.acceptAllBlocks,
                            disabled: resolveMany.isPending || unsettledBlocks.length === 0,
                            onClick: ()=>resolveMany.mutate({
                                    changeSetId,
                                    companyId,
                                    itemIds: unsettledBlocks.map((b)=>b.itemId),
                                    resolution: 'Accepted'
                                }),
                            variant: "outline",
                            children: resolveMany.isPending ? 'Accepting…' : `Accept all ${unsettledBlocks.length} (${unsettledRates.toLocaleString()} rates)`
                        }),
                        description: "A block is one identity — company, period, worksite, burden, schedule line. Accepting one accepts every rate in it.",
                        drawerEyebrow: summary.title || 'Pricing sheet',
                        renderAll: ()=>renderBlocks(blocks),
                        testId: Testids.pricingSheetReview.blocksSection,
                        title: "Rate blocks to decide",
                        total: blocks.length,
                        viewAllTestId: Testids.pricingSheetReview.blocksViewAll,
                        children: renderBlocks(blocks.slice(0, PEEK_ROWS))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                eyebrow: openBlock ? /*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex items-center gap-[var(--space-1-5)]",
                    children: [
                        /*#__PURE__*/ _jsxs("span", {
                            className: "font-mono tracking-[0.02em] tabular-nums",
                            children: [
                                openBlock.rates.toLocaleString(),
                                " rates"
                            ]
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": true,
                            children: "·"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: openBlock.sheetName || 'this document'
                        })
                    ]
                }) : 'Rate block',
                headerActions: // Beside the title, not buried in the body. Seeing the page is how a
                // reviewer checks the block rather than takes its word for it, so it
                // sits where the eye already is.
                canShowBlockSource ? /*#__PURE__*/ _jsx(ShowOnPageButton, {
                    onClick: ()=>setSourceView({
                            blockId: openBlock?.sourceBlockId ?? '',
                            page: openBlock?.sourcePage ?? 1
                        }),
                    page: openBlock?.sourcePage,
                    testId: Testids.pricingSheetReview.viewSource,
                    what: "this rate block"
                }) : undefined,
                // NON-MODAL while the page viewer is beside it, and only then.
                //
                // A modal drawer's scrim is `fixed inset-0` above everything, and Radix
                // also marks outside content aria-hidden and kills its pointer events —
                // so the viewer was unreachable under the scrim, and every click aimed at
                // it landed on the scrim, which Radix read as an outside interaction and
                // used to dismiss the drawer. Both panels vanished on the first click.
                // See the guard below for which outside clicks may still dismiss.
                modal: !sourceView,
                contentProps: {
                    onInteractOutside: keepOpenWhenInteractingWithViewer,
                    onPointerDownOutside: keepOpenWhenInteractingWithViewer
                },
                onOpenChange: (open)=>{
                    if (!open) {
                        setOpenBlockId(null);
                        // The page belongs to the block. Leaving it up over an empty drawer
                        // would strand a viewer on a document nothing is asking about.
                        setSourceView(null);
                    }
                },
                open: Boolean(openBlock),
                testId: Testids.pricingSheetReview.blockDrawer,
                title: openBlock?.identity || 'Rate block',
                width: "lg",
                ...viewOnly || !openBlock ? {} : {
                    footerProps: {
                        confirmLabel: `Accept ${openBlock.rates.toLocaleString()} rates`,
                        confirmProps: {
                            'data-testid': Testids.pricingSheetReview.blockAccept,
                            disabled: resolveBlock.isPending
                        },
                        cancelLabel: 'Reject',
                        cancelProps: {
                            'data-testid': Testids.pricingSheetReview.blockReject,
                            disabled: resolveBlock.isPending
                        },
                        onConfirm: ()=>ruleOn(openBlock.itemId, 'Accepted'),
                        onCancel: ()=>ruleOn(openBlock.itemId, 'Rejected')
                    }
                },
                children: openBlock ? /*#__PURE__*/ _jsx(BlockDetail, {
                    block: openBlock,
                    note: note,
                    onNoteChange: setNote,
                    viewOnly: viewOnly
                }) : null
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                eyebrow: openReading ? `Reading · ${openReading.sheetName || 'sheet'}` : 'Reading',
                headerActions: openReading && summary.sourceDocumentId && openReading.sourcePage > 0 ? /*#__PURE__*/ _jsx(ShowOnPageButton, {
                    onClick: ()=>setSourceView({
                            blockId: openReading.sourceBlockId,
                            page: openReading.sourcePage
                        }),
                    page: openReading.sourcePage,
                    what: "this sheet"
                }) : undefined,
                onOpenChange: (open)=>{
                    if (!open) {
                        setOpenReadingId(null);
                        setSourceView(null);
                    }
                },
                open: Boolean(openReading),
                testId: Testids.pricingSheetReview.readingDrawer,
                title: `How ${openReading?.sheetName || 'this sheet'} was read`,
                width: "lg",
                modal: !sourceView,
                contentProps: {
                    onInteractOutside: keepOpenWhenInteractingWithViewer,
                    onPointerDownOutside: keepOpenWhenInteractingWithViewer
                },
                children: openReading ? /*#__PURE__*/ _jsx(ReadingDetail, {
                    reading: openReading
                }) : null
            }),
            sourceView && summary.sourceDocumentId ? /*#__PURE__*/ _jsx(PricingSheetSourceDrawer, {
                // Beside the block drawer, not on top of it: the identity being ruled
                // on and the page it came from are only useful together. 672px is the
                // drawer's own width at `lg` (sm:max-w-2xl).
                besideDrawerWidth: openBlock ? 672 : undefined,
                blockId: sourceView.blockId,
                companyId: companyId,
                filename: summary.title || 'Pricing sheet',
                isOpen: true,
                onClose: ()=>setSourceView(null),
                page: sourceView.page,
                sourceDocumentId: summary.sourceDocumentId
            }) : null
        ]
    });
}
function Field({ label, children }) {
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "text-[length:var(--fs-ui-eyebrow)] text-muted-foreground",
                children: label
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-[var(--space-0-5)] text-[length:var(--fs-ui)] tabular-nums text-foreground",
                children: children
            })
        ]
    });
}
/** One reading in full — the axes, the self-check, and what was refused. */ function ReadingDetail({ reading }) {
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.pricingSheetReview.reading,
        children: [
            reading.titleText ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                label: "The sheet calls itself",
                value: reading.titleText
            }) : null,
            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                label: "Company",
                value: reading.companyAxis || '—'
            }),
            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                label: "Period",
                value: reading.periodAxis || '—'
            }),
            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                label: "Worksite",
                value: reading.worksiteAxis || '—'
            }),
            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                label: "Self-check",
                value: reading.validation || '—'
            }),
            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                label: "Columns",
                value: reading.columns.length > 0 ? reading.columns.join('  ·  ') : '—'
            }),
            reading.refusals.length > 0 ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                label: "Deliberately not extracted",
                value: reading.refusals.join('  ·  ')
            }) : null
        ]
    });
}
/** One block: what rides on it, a spot-check, and the note carried on the decision. */ function BlockDetail({ block, note, onNoteChange, viewOnly }) {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-[var(--space-card)] flex flex-wrap gap-[var(--space-1-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: SEVERITY_VARIANT[block.severity] ?? 'neutral',
                        children: block.severity || 'Unclassified'
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: block.resolutionState === 'Accepted' ? 'success' : block.resolutionState === 'Rejected' ? 'danger' : 'neutral',
                        children: block.resolutionState
                    }),
                    block.unresolved > 0 ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: "warning",
                        children: [
                            block.unresolved,
                            " unregistered"
                        ]
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "This block"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-[var(--space-card)] gap-y-3",
                children: [
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Rates",
                        children: block.rates.toLocaleString()
                    }),
                    /*#__PURE__*/ _jsx(Field, {
                        label: "Where",
                        children: block.sheetName || '—'
                    }),
                    block.unresolved > 0 ? /*#__PURE__*/ _jsxs(Field, {
                        label: "Not staged",
                        children: [
                            block.unresolved,
                            " whose category is not in the registry"
                        ]
                    }) : null
                ]
            }),
            block.reason ? /*#__PURE__*/ _jsx("p", {
                className: "mt-3 text-[length:var(--fs-ui)] text-muted-foreground",
                children: block.reason
            }) : null,
            /*#__PURE__*/ _jsxs("section", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Spot-check"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 text-[length:var(--fs-ui-eyebrow)] text-muted-foreground",
                        children: "The values are read bit-exact, so what is worth checking is whether each rate is attached to the right JOB — not whether the number was typed correctly."
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-2",
                        children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                            columns: [
                                {
                                    id: 'lcat',
                                    header: 'Labor category',
                                    cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    children: r.laborCategory
                                                }),
                                                r.description ? /*#__PURE__*/ _jsx("div", {
                                                    className: "max-w-[44ch] text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                                    children: r.description
                                                }) : null,
                                                r.qualifications ? /*#__PURE__*/ _jsx("div", {
                                                    className: "whitespace-pre-line text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                                    children: r.qualifications
                                                }) : r.description ? null : /*#__PURE__*/ _jsx("div", {
                                                    className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                                    children: "No definition on this entry"
                                                })
                                            ]
                                        })
                                },
                                {
                                    align: 'right',
                                    id: 'amount',
                                    header: 'Amount',
                                    cell: (r)=>r.amount.toFixed(2)
                                },
                                {
                                    id: 'unit',
                                    header: 'Unit',
                                    cell: (r)=>r.rateUnit || '—'
                                },
                                {
                                    id: 'period',
                                    header: 'Period',
                                    cell: (r)=>r.periodLabel || '—'
                                },
                                {
                                    id: 'sin',
                                    header: 'SIN',
                                    cell: (r)=>r.sin || '—'
                                }
                            ],
                            emptyTitle: "No sample available",
                            getRowId: (r)=>`${r.laborCategory}-${r.amount}-${r.sin}`,
                            rows: block.sample
                        })
                    })
                ]
            }),
            viewOnly ? null : /*#__PURE__*/ _jsxs("section", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: "Note"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 text-[length:var(--fs-ui-eyebrow)] text-muted-foreground",
                        children: "Recorded with the decision. The buttons are in the footer."
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        className: "mt-2",
                        "data-testid": Testids.pricingSheetReview.blockNote,
                        onChange: (e)=>onNoteChange(e.target.value),
                        placeholder: "Why — recorded with the decision.",
                        value: note
                    })
                ]
            })
        ]
    });
}
